/**
 * attentionStore — GM Attention Phase 1 (2026-07-17).
 *
 * Holds the live agent roster + pending approvals pushed from the per-tenant
 * vibe-backend /api/attention/stream WS. The backend sends the FULL roster on
 * every change (sessions + non-resolved approvals), so this store is a plain
 * last-write-wins snapshot — no client-side merge/reduce needed, which keeps a
 * reconnecting client consistent after exactly one frame.
 *
 * Powers the LiveAgentsDock overlay (and, later, the GM Mobile iPhone app which
 * hits the same endpoints).
 */
import { create } from 'zustand';

export interface AttentionSession {
  session_id: string;
  agent_type: string;
  display_name: string;
  state: 'running' | 'needs_approval' | 'needs_input' | 'idle' | 'done' | 'error';
  last_activity: string;
  updated_at: number;
}

export interface AttentionApproval {
  approval_id: string;
  session_id: string;
  kind: string;
  title: string;
  detail: string;
  command: string;
  created_at: number;
  /** Phase 2 — provenance + rendering hints. meta.answerable === false marks a
   * read-only mirror card (hermes' own terminal prompt): render "answer in
   * terminal" instead of Approve/Deny. meta.origin names the source adapter. */
  meta?: { origin?: string; answerable?: boolean; [k: string]: unknown };
}

/** Phase 2 — adapter health pushed with every roster frame.
 * name -> 'connected' | 'disconnected' | 'disabled' */
export type AttentionAdapters = Record<string, string>;

interface AttentionState {
  sessions: AttentionSession[];
  approvals: AttentionApproval[];
  adapters: AttentionAdapters;
  connected: boolean;
  open: boolean;
  setRoster: (roster: {
    sessions: AttentionSession[];
    approvals: AttentionApproval[];
    adapters?: AttentionAdapters;
  }) => void;
  setConnected: (connected: boolean) => void;
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
}

export const useAttentionStore = create<AttentionState>((set) => ({
  sessions: [],
  approvals: [],
  adapters: {},
  connected: false,
  open: false,
  setRoster: (roster) =>
    set({
      sessions: roster.sessions ?? [],
      approvals: roster.approvals ?? [],
      adapters: roster.adapters ?? {},
    }),
  setConnected: (connected) => set({ connected }),
  toggleOpen: () => set((s) => ({ open: !s.open })),
  setOpen: (open) => set({ open }),
}));

/** Pure selector — number of approvals awaiting a decision (badge count). */
export function pendingCount(state: Pick<AttentionState, 'approvals'>): number {
  return state.approvals.length;
}
